import { useState, memo, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { manageAppsAPI } from 'services/manageApps.service';
import * as S from './ChangeApp.styled';
import { Content } from './Content';
import {
  ModalBase,
  ModalHeader,
  ModalFooter,
  NotificationModal,
  ChooseBase,
  Buttons,
  Loader,
} from 'components';
import { useFacade } from './useFacade';
import { appNumberToPromote } from 'store/selectors';

const ChangeApp = ({ isWebsite = false }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const selectedItem = useSelector(appNumberToPromote);
  const currentIsWebsite = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.isWebsite,
  );

  const { data: list, isFetching } = manageAppsAPI.useFetchAppsToPromoteByFilterQuery(isWebsite);

  const sortedAppListByAlphabet = useMemo(() => {
    if (!list) return [];
    if (list && Array.isArray(list) && list.length > 0) {
      return [...list]?.sort((a, b) => {
        const nameA = a.pba_name.trim().toLowerCase();
        const nameB = b.pba_name.trim().toLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    }
  }, [list]);

  const {
    handleSearchOnChange,
    handleClearSearchValue,
    handleClose,
    faundedApps,
    choosedApp,
    setChoosedApp,
    handleClickApplyBtn,
    btnLabel,
  } = useFacade(search, setSearch, setIsOpen, sortedAppListByAlphabet, isWebsite);

  const shouldWarnOnTypeSwitch = Boolean(selectedItem) && currentIsWebsite !== isWebsite;

  const handleOpen = useCallback(() => {
    if (shouldWarnOnTypeSwitch) {
      setIsWarningOpen(true);
      return;
    }
    setIsOpen(true);
  }, [shouldWarnOnTypeSwitch]);

  const handleWarningContinue = useCallback(() => {
    setIsWarningOpen(false);
    setIsOpen(true);
  }, []);

  return (
    <>
      <Buttons.SecondaryNormal
        style={{ width: '100%' }}
        id='changeApp'
        name='changeApp'
        onClick={handleOpen}
      >
        {btnLabel}
      </Buttons.SecondaryNormal>
      {isWarningOpen && (
        <NotificationModal
          onCancel={handleWarningContinue}
          onConfirm={handleWarningContinue}
          title="Switching between 'App' and 'Website'"
          iconType={NotificationModal.iconTypes.warning}
          iconSign='!'
          confirmBtnType={NotificationModal.btnTypes.PrimaryNormal}
          confirmBtnText='Ok'
        >
          All type-specific data (like URL or Package selection) will be cleared.
        </NotificationModal>
      )}
      {isOpen && (
        <ModalBase>
          <S.Box>
            <ModalHeader title='Application' close={handleClose} />
            <S.Content>
              <ChooseBase.OneSection
                searchValue={search}
                searchOnChange={handleSearchOnChange}
                handleClearSearchValue={handleClearSearchValue}
                dontShowHeader
                title='Search App'
              >
                {isFetching && <Loader.Local delay={0.01} />}
                {!isFetching && (
                  <Content
                    foundedApps={faundedApps}
                    search={search}
                    isWebsite={isWebsite}
                    handleClose={handleClose}
                    choosedApp={choosedApp}
                    setChoosedApp={setChoosedApp}
                  />
                )}
              </ChooseBase.OneSection>
            </S.Content>
            <ModalFooter
              saveButtonText='Apply'
              saveButtonOnClick={handleClickApplyBtn}
              close={handleClose}
              saveButtonDisabled={!choosedApp}
            />
          </S.Box>
        </ModalBase>
      )}
    </>
  );
};

export default memo(ChangeApp);
